// Account-security queries for the shell's own screens (/account, /2fa/setup).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client, unwrap } from "@/api";

export const sessionKeys = { all: ["auth", "sessions"] as const };

export function useSessionsQuery() {
  return useQuery({
    queryKey: sessionKeys.all,
    queryFn: () => unwrap(client.GET("/api/v1/auth/sessions")),
  });
}

export function useTerminateSessionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      unwrap(client.DELETE("/api/v1/auth/sessions/{token}", { params: { path: { token } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKeys.all }),
  });
}

export type MePatch = { display_name?: string; current_password?: string; new_password?: string };

/**
 * PATCH auth/me → Me. A password change signs out every OTHER session (the current one stays),
 * so the sessions list is refetched. The caller refreshes the session's `me` for name changes.
 */
export function useUpdateMeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MePatch) => unwrap(client.PATCH("/api/v1/auth/me", { body })),
    onSuccess: (_me, body) => (body.new_password ? qc.invalidateQueries({ queryKey: sessionKeys.all }) : undefined),
  });
}

/** Staged TOTP enrolment: secret + backup codes. Held in component state only. */
export const startTotpSetup = () =>
  unwrap<{ secret: string; backup_codes: string[] }>(client.POST("/api/v1/auth/2fa/setup"));
export const enableTotp = (code: string) => unwrap(client.POST("/api/v1/auth/2fa/enable", { body: { code } }));
